import React, { useState, useContext, useEffect, useRef } from 'react';
import { GlobalDataContext, TemplateContext, evaluateTemplateValue } from "@ea-lab/reactive-json";
import { ProjectSettingsModal } from '../project';
import NewTaskModal from '../task/NewTaskModal';
import { buildTaskTypesMap } from '../shared';
import { Archive, LayoutDashboard, Settings } from 'lucide-react';
import { readShowArchived, writeShowArchived, formatArchiveDate } from '../../services/archive';
import { buildProjectSettingsPatch, postProjectSettings, projectSaveErrorMessage } from '../../services/projectSettings';

// Resolve an options-array prop. evaluateTemplateValue returns the raw "~.x"
// string for relative paths passed to a custom component, so fall back to the
// global data tree (where the page datasources write, e.g. ~~.returnTypeOptions).
// Same pattern as the tags fallback in KanbanBoard.
const resolveArrayProp = (propVal, key, gdc, tc) => {
  let v = evaluateTemplateValue({ valueToEvaluate: propVal, globalDataContext: gdc, templateContext: tc });
  if (!Array.isArray(v)) v = gdc?.data?.[key];
  return Array.isArray(v) ? v : [];
};

const KanbanHeader = ({ props }) => {
  const globalDataContext = useContext(GlobalDataContext);
  const templateContext = useContext(TemplateContext);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const [selectedTaskType, setSelectedTaskType] = useState('');

  const [isNewTaskMenuOpen, setIsNewTaskMenuOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(readShowArchived);
  const dropdownRef = React.useRef(null);

  const pageTitle = evaluateTemplateValue({
      valueToEvaluate: props.pageTitle,
      globalDataContext,
      templateContext
  }) || 'Projet';

  const currentSettings = evaluateTemplateValue({
      valueToEvaluate: props.settings,
      globalDataContext,
      templateContext
  }) || {};

  const propUsers = evaluateTemplateValue({
      valueToEvaluate: props.users,
      globalDataContext,
      templateContext
  });

  const users = propUsers || currentSettings.users || [];

  // Scope the creation-modal assignee to the project's members (users with
  // access). Falls back to all users when the project has no members so the
  // field never becomes empty.
  // Read project memberIds reactively (the GlobalDataContext has no `.data`
  // member — only the store/templateData).
  const rawProjectMemberIds = evaluateTemplateValue({ valueToEvaluate: '~~.project.memberIds', globalDataContext, templateContext });
  const projectMemberIds = Array.isArray(rawProjectMemberIds) ? rawProjectMemberIds : [];
  const memberUsers = (Array.isArray(users) && projectMemberIds.length)
      ? users.filter(u => projectMemberIds.includes(Number(u.id)))
      : users;

  const currentUserRole = evaluateTemplateValue({
      valueToEvaluate: props.currentUserRole,
      globalDataContext,
      templateContext
  });

  const roles = evaluateTemplateValue({
      valueToEvaluate: props.roles,
      globalDataContext,
      templateContext
  }) || [];

  // T40 (CDC-TMA #28/#8): the user's REAL project role (from /api/project/{id}/role).
  // currentUserRole is initialised to it once; the "En tant que" selector then
  // lets a manager *simulate* another role. Security stays server-side, so the
  // simulation is a read-only visual preview.
  const realUserRole = evaluateTemplateValue({
      valueToEvaluate: props.realUserRole,
      globalDataContext,
      templateContext
  }) || '';

  const roleInitDone = useRef(false);
  useEffect(() => {
      if (roleInitDone.current || !realUserRole) return;
      roleInitDone.current = true;
      if (globalDataContext.updateData) {
          globalDataContext.updateData(realUserRole, 'currentUserRole');
      }
  }, [realUserRole]);

  const SIM_MANAGER_ROLES = ['Administrateur', 'Chef de projet', 'CDP', 'project-admin', 'project-cdp'];
  const canSimulateRole = SIM_MANAGER_ROLES.includes(realUserRole);
  const isSimulating = !!realUserRole && !!currentUserRole && currentUserRole !== realUserRole;
  // Les paramètres du projet (renommer le groupe, gérer les membres, la matrice
  // de droits) sont réservés à l'ADMINISTRATEUR du projet — le rôle de groupe
  // `project-admin`, seul à porter `edit group` / `administer members` côté
  // serveur. Tant que le rôle réel n'est pas revenu de /api/project/{id}/role,
  // le bouton reste masqué : l'afficher par défaut promettrait un écran que le
  // serveur refuserait d'enregistrer.
  const ADMIN_ROLES = ['Administrateur', 'project-admin'];
  const isProjectAdmin = ADMIN_ROLES.includes(currentUserRole);

  const taskTypes = resolveArrayProp(props.taskTypes, 'taskTypes', globalDataContext, templateContext);
  const returnTypeOptions = resolveArrayProp(props.returnTypeOptions, 'returnTypeOptions', globalDataContext, templateContext);
  const evolutionTypeOptions = resolveArrayProp(props.evolutionTypeOptions, 'evolutionTypeOptions', globalDataContext, templateContext);
  const tagOptions = resolveArrayProp(props.tagOptions, 'tagOptions', globalDataContext, templateContext);
  const typeOptions = resolveArrayProp(props.typeOptions, 'typeOptions', globalDataContext, templateContext);
  const methodologyOptions = resolveArrayProp(props.methodologyOptions, 'methodologyOptions', globalDataContext, templateContext);
  const accountOptions = resolveArrayProp(props.accountOptions, 'accountOptions', globalDataContext, templateContext);

  // Task types the CURRENT user may create here, straight from the server
  // (/api/project/{id}/role, driven by the project rights matrix). It reflects
  // the real role, not the simulated one: the "En tant que" selector is a
  // visual preview and the server never simulates.
  const rawCreatableTypes = evaluateTemplateValue({ valueToEvaluate: '~~.currentUserRoleData.creatableTypes', globalDataContext, templateContext });
  const creatableTypeIds = Array.isArray(rawCreatableTypes) ? rawCreatableTypes.map(Number) : null;

  const enabledTaskTypes = currentSettings.enabledTaskTypes || {};
  const { meta: creationTypeMeta } = buildTaskTypesMap(taskTypes);
  const activeTaskTypes = Object.entries(enabledTaskTypes)
    .filter(([type, enabled]) => {
      if (!enabled) return false;

      // The matrix forbids creating this type → do not offer it. Tolerant: an
      // unresolved tid (type map not loaded yet) never hides a type.
      if (creatableTypeIds && creatableTypeIds.length) {
        const tid = Number(creationTypeMeta[type]?.tid);
        if (tid && !creatableTypeIds.includes(tid)) return false;
      }

      return true;
    })
    .map(([type]) => type)
    // N'afficher comme création de tâche-MÈRE que les types de TÊTE : un nom de
    // sous-type (parentId > 0) ne doit pas apparaître comme type de tâche-mère
    // (hiérarchie incohérente). Tolérant : si typeOptions n'est pas encore chargé,
    // on ne filtre pas. Un libellé partagé top-level/sous-type reste affiché dès
    // qu'AU MOINS un type de tête le porte.
    .filter((name) => typeOptions.length === 0
      || typeOptions.some((o) => o.label === name && !Number(o.data?.parentId)));

  // Close dropdown when clicking outside (simple version)
  React.useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsNewTaskMenuOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownRef]);

  const handleSaveSettings = async (newSettings) => {
      // CDC-TMA T14/T15: persist the project title (Group label) and description
      // when they changed. The server enforces "edit group" — a client gets 403.
      // The diff itself lives in services/projectSettings.js: the projects list
      // mounts the SAME modal and must write exactly the same fields.
      const projectId = getProjectId();
      const readProject = (key) =>
        evaluateTemplateValue({ valueToEvaluate: `~~.project.${key}`, globalDataContext, templateContext }) || '';

      const patchData = buildProjectSettingsPatch({
        current: {
          name: pageTitle,
          description: readProject('description'),
          picto: readProject('picto'),
          methodology: readProject('methodology'),
          account: readProject('account'),
          hosts: evaluateTemplateValue({ valueToEvaluate: '~~.project.hosts', globalDataContext, templateContext }) || [],
          agentInstance: readProject('agentInstance'),
          projectSettings: readProject('projectSettings'),
          enabledTaskTypes: currentSettings.enabledTaskTypes || {},
          workingHours: currentSettings.workingHours || {},
        },
        next: newSettings,
        taskTypes,
      });

      if (projectId && Object.keys(patchData).length > 0) {
        try {
          const resp = await postProjectSettings(projectId, patchData);
          if (!resp.ok) {
            alert(projectSaveErrorMessage(resp.status));
          } else if (globalDataContext.updateData) {
            // Refresh the views that show the project name / description.
            if (patchData.name) {
              globalDataContext.updateData(patchData.name, 'pageTitle');
              globalDataContext.updateData(patchData.name, 'project.name');
            }
            if (patchData.description !== undefined) {
              globalDataContext.updateData(patchData.description, 'project.description');
            }
            if (patchData.picto !== undefined) {
              globalDataContext.updateData(patchData.picto, 'project.picto');
            }
            if (patchData.projectSettings !== undefined) {
              globalDataContext.updateData(patchData.projectSettings, 'project.projectSettings');
            }
            // Sinon la pop-in rouverte montrerait l'ancienne adresse (le serveur
            // la remet en forme canonique : elle s'affiche ainsi au rechargement).
            if (patchData.agentInstance !== undefined) {
              globalDataContext.updateData(patchData.agentInstance, 'project.agentInstance');
            }
          }
        } catch (e) {
          console.error('Project settings save failed', e);
        }
      }

      // Update the local settings part (unchanged behaviour).
      if (globalDataContext.updateData) {
        globalDataContext.updateData(newSettings, "settings");
      } else if (globalDataContext.setData) {
        const currentData = { ...globalDataContext.data };
        currentData.settings = newSettings;
        globalDataContext.setData(currentData);
      }
  };

  const handleRoleChange = (e) => {
    const newRole = e.target.value;
    if (globalDataContext.updateData) {
      globalDataContext.updateData(newRole, "currentUserRole");
    } else if (globalDataContext.setData) {
       const currentData = { ...globalDataContext.data };
       currentData.currentUserRole = newRole;
       globalDataContext.setData(currentData);
    }
  };

  const getProjectId = () => {
    if (props.projectId) return props.projectId;
    if (props.project && props.project.id) return props.project.id;
    const evaluated = evaluateTemplateValue({
        valueToEvaluate: "~~.project.id",
        globalDataContext,
        templateContext,
    });
    if (evaluated && typeof evaluated !== 'string') return evaluated;
    if (typeof window !== 'undefined') {
        const params = new URLSearchParams(window.location.search);
        return params.get('projectId') || params.get('id');
    }
    return null;
  };

  const handleCreateTask = (taskData) => {
    // Resolve against members only, so a stale free-typed name cannot assign a non-member.
    const user = memberUsers.find(u => u.name === taskData.assignee);
    const projectId = getProjectId();

    const data = {
      title: taskData.title,
      description: taskData.description || "",
      // Workflow initial state is assigned server-side on create (presave →
      // first state of the type's workflow), so the client no longer sends it.
      assignee: user ? parseInt(user.id) : null,
      type: taskData.type,
      returnType: taskData.returnType || null,
      evolutionType: taskData.evolutionType || null,
      tags: taskData.tags || [],
      projectId: projectId ? parseInt(projectId) : null,
      // Échéance (field_due_date, date-only) : la popin rend déjà du YYYY-MM-DD.
      dueDate: taskData.dueDate || null,
    };

    // priority/complexity are OPTIONAL: OMIT the key when unset. Sending 0 would
    // violate the field min:1 (HTTP 422); an omitted key persists empty now that
    // Drupal's default_value:1 is removed.
    const priority = parseInt(taskData.rating, 10);
    if (Number.isInteger(priority) && priority >= 1) data.priority = priority;
    const complexity = parseInt(taskData.complexity, 10);
    if (Number.isInteger(complexity) && complexity >= 1) data.complexity = complexity;

    // Deferred file attachments — fids of the temporary files uploaded in the modal.
    if (Array.isArray(taskData.attachedFiles) && taskData.attachedFiles.length) {
      data.attachedFiles = taskData.attachedFiles;
    }

    if (taskData.parent_task) {
      data.parentTaskId = parseInt(taskData.parent_task);
    }

    if (globalDataContext.updateData) {
      // Update only newTask.data — keeps submission_url (set in the YAML) intact.
      // Writing the whole newTask object would lose submission_url because
      // globalDataContext.data is not exposed by the reactive-json context.
      globalDataContext.updateData(data, "newTask.data");
      globalDataContext.updateData(true, "saveTaskNow");
    }
  };

  const projectArchived = evaluateTemplateValue({
    valueToEvaluate: '~~.project.archived',
    globalDataContext,
    templateContext,
  }) || null;

  // The board's empty state ("aucune tâche") REUSES this very button instead of
  // duplicating the type dropdown / NewTaskModal / creation logic: it fires
  // `kanbanOpenNewTask` on the shared kanban* CustomEvent bus and we simply open
  // the existing dropdown — exactly as if the user had clicked "+ Nouveau".
  useEffect(() => {
    const handler = () => {
      if (!projectArchived) setIsNewTaskMenuOpen(true);
    };
    document.addEventListener('kanbanOpenNewTask', handler);
    return () => document.removeEventListener('kanbanOpenNewTask', handler);
  }, [projectArchived]);

  // Tableau de bord du projet (/projets/:id) : le board est toujours scopé à un
  // projet, mais son en-tête n'offrait aucun retour vers ses KPI (complétion,
  // retards, heures passées). L'id vient du seed d'URL / du store, donc il peut
  // être absent au tout premier rendu : on masque alors le lien.
  const projectId = getProjectId();

  return (
    <>
      <div className="bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between">
        {/* Left Side */}
        <div className="flex items-center gap-4">
          <div className="relative" ref={dropdownRef}>
            <button 
              onClick={() => !projectArchived && setIsNewTaskMenuOpen(!isNewTaskMenuOpen)}
              disabled={!!projectArchived}
              title={projectArchived ? 'Projet archivé — création désactivée' : undefined}
              className="btn btn-primary"
            >
              <span className="font-bold text-lg leading-none pb-1">+</span>
              <span>Nouveau</span>
              <svg 
                className={`w-4 h-4 transition-transform ${isNewTaskMenuOpen ? 'rotate-180' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            
            {isNewTaskMenuOpen && (
              <div className="absolute top-full left-0 mt-1 w-56 bg-white rounded-md shadow-lg border border-slate-200 z-50 py-1">
                {activeTaskTypes.length > 0 ? (
                  activeTaskTypes.map(label => {
                    const tid = typeOptions.find(o => o.label === label)?.value ?? typeOptions[0]?.value;
                    return (
                    <button
                      key={tid}
                      className="block w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-iris-50 hover:text-iris-700"
                      onClick={() => {
                        setSelectedTaskType(tid);
                        setIsNewTaskModalOpen(true);
                        setIsNewTaskMenuOpen(false);
                      }}
                    >
                      {label}
                    </button>
                  );})
                ) : (
                  <div className="px-4 py-2 text-sm text-slate-400 italic">Aucun type de tâche activé</div>
                )}
              </div>
            )}
          </div>
          
          <div>
            <div className="flex items-center gap-2">
              <span className="font-bold text-slate-900 text-lg">{pageTitle}</span>
              {/* Archived marker: the board is otherwise indistinguishable from a
                  live one — only "+ Nouveau" being disabled hinted at it. */}
              {projectArchived && (
                <span
                  className="badge badge-warning text-2xs"
                  aria-label={`Projet archivé le ${formatArchiveDate(projectArchived)}`}
                >
                  <Archive size={12} />
                  Archivé le {formatArchiveDate(projectArchived)}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Right Side */}
        <div className="flex items-center gap-4">
          {/* Retour au tableau de bord du projet. Pas de gate de rôle : /projets/:id
              exige la même permission (`use projects`) que ce board, et l'accès au
              projet lui-même reste arbitré côté serveur. */}
          {projectId && (
            <a
              href={`/projets/${projectId}`}
              title="Tableau de bord du projet"
              className="flex items-center gap-2 px-3 py-2 text-sm rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-100 transition-colors"
            >
              <LayoutDashboard size={16} aria-hidden="true" />
              <span className="hidden sm:inline">Tableau de bord</span>
              <span className="sr-only sm:hidden">Tableau de bord du projet</span>
            </a>
          )}
          {/* Voir / masquer les tâches archivées (toggle partagé avec docs/projets). */}
          <button
            type="button"
            onClick={() => {
              const next = !showArchived;
              setShowArchived(next);
              writeShowArchived(next);
              document.dispatchEvent(new CustomEvent('kanbanShowArchivedChanged', { detail: { showArchived: next } }));
            }}
            aria-pressed={showArchived}
            title={showArchived ? 'Masquer les tâches archivées' : 'Voir les tâches archivées'}
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg transition-colors ${showArchived ? 'text-amber-700 bg-amber-50 hover:bg-amber-100' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
          >
            <Archive size={16} />
            <span className="hidden sm:inline">{showArchived ? 'Masquer les archivées' : 'Voir les archivées'}</span>
          </button>
          {/* Project actions — flattened from the old "Actions du projet" (⋮) menu.
              Only Paramètres remains here (admin-gated, icon-only): the Corbeille
              link went away with the trash feature — archiving is now the only way
              out. Feuille de temps lives in the navbar user menu; Documentation in
              the Docs section. */}
          {isProjectAdmin && (
            <button
              type="button"
              onClick={() => setIsSettingsOpen(true)}
              title="Paramètres du projet"
              aria-label="Paramètres du projet"
              className="flex items-center justify-center w-9 h-9 rounded-lg text-stone-500 hover:text-stone-900 hover:bg-stone-100 transition-colors"
            >
              <Settings size={18} />
            </button>
          )}
          {/* T40: "Voir en tant que" (simulation de rôle) — masqué pour le moment,
              à réactiver en V2 pour les tests. La logique de rôle réel reste active
              (currentUserRole est initialisé au rôle réel via le useEffect d'init). */}
          {/*
          {canSimulateRole && roles && roles.length > 0 ? (
            <div className="flex items-center gap-2">
              <label htmlFor="role-select" className="text-sm font-medium text-gray-700">Voir en tant que :</label>
              <select
                id="role-select"
                value={currentUserRole || ''}
                onChange={handleRoleChange}
                className={`block rounded-md border-0 py-1.5 pl-3 pr-10 text-gray-900 ring-1 ring-inset focus:ring-2 focus:ring-purple-600 sm:text-sm sm:leading-6 ${isSimulating ? 'ring-amber-400 bg-amber-50' : 'ring-gray-300'}`}
              >
                {roles.map((role) => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </select>
              {isSimulating && (
                <button
                  type="button"
                  onClick={() => globalDataContext.updateData && globalDataContext.updateData(realUserRole, 'currentUserRole')}
                  className="text-xs text-amber-700 font-medium hover:underline whitespace-nowrap"
                  title={`Simulation active — revenir à votre rôle (${realUserRole})`}
                >
                  ⮌ {realUserRole}
                </button>
              )}
            </div>
          ) : null}
          */}
        </div>

      </div>

      <ProjectSettingsModal  
        isOpen={isSettingsOpen} 
        onClose={() => setIsSettingsOpen(false)}
        title={pageTitle}
        projectId={getProjectId()}
        description={evaluateTemplateValue({ valueToEvaluate: '~~.project.description', globalDataContext, templateContext }) || ''}
        picto={evaluateTemplateValue({ valueToEvaluate: '~~.project.picto', globalDataContext, templateContext }) || ''}
        methodology={evaluateTemplateValue({ valueToEvaluate: '~~.project.methodology', globalDataContext, templateContext }) || ''}
        account={evaluateTemplateValue({ valueToEvaluate: '~~.project.account', globalDataContext, templateContext }) || ''}
        methodologyOptions={methodologyOptions}
        accountOptions={accountOptions}
        hosts={evaluateTemplateValue({ valueToEvaluate: '~~.project.hosts', globalDataContext, templateContext }) || []}
        agentInstance={evaluateTemplateValue({ valueToEvaluate: '~~.project.agentInstance', globalDataContext, templateContext }) || ''}
        onAccountCreated={(option) => {
          if (globalDataContext.updateData) globalDataContext.updateData([...accountOptions, option], 'accountOptions');
        }}
        settings={currentSettings}
        onSave={handleSaveSettings}
        users={users}
        taskTypes={Array.isArray(taskTypes) ? taskTypes : []}
        canEditWorkflow={currentUserRole === 'Administrateur'}
        onMembersChanged={(memberIds) => {
          // Keep the in-memory project.memberIds in sync so the assignee filter /
          // creation modal reflect membership changes without a page reload.
          if (globalDataContext.updateData) globalDataContext.updateData(memberIds, 'project.memberIds');
        }}
      />
      
      <NewTaskModal
        isOpen={isNewTaskModalOpen}
        onClose={() => setIsNewTaskModalOpen(false)}
        onSave={handleCreateTask}
        taskType={selectedTaskType}
        typeOptions={typeOptions}
        enabledTaskTypes={Object.fromEntries(activeTaskTypes.map((t) => [t, true]))}
        users={memberUsers}
        returnTypeOptions={returnTypeOptions}
        evolutionTypeOptions={evolutionTypeOptions}
        tagOptions={tagOptions}
      />
    </>
  );
};

export default KanbanHeader;
